import React, { useEffect, useRef } from 'react';
import Typed from 'typed.js';
import './CodeBackground.css';

const CodeBackground = () => {
  const frontendCodeRef = useRef(null);
  const sqlCodeRef = useRef(null);
  const backendCodeRef = useRef(null);
  const frontendTypedRef = useRef(null);
  const sqlTypedRef = useRef(null);
  const backendTypedRef = useRef(null);

  const frontendCode = `
// React Frontend
import React, { useState, useEffect } from 'react';

const Portfolio = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const response = await fetch('/api/projects');
      const data = await response.json();
      setProjects(data);
      setLoading(false);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  return (
    <div className="portfolio">
      {loading ? (
        <Loader />
      ) : (
        projects.map(project => (
          <ProjectCard 
            key={project.id}
            title={project.title}
            description={project.description}
            technologies={project.tech}
          />
        ))
      )}
    </div>
  );
};`;

  const sqlCode = `
-- Complex SQL Queries
WITH ProjectStats AS (
  SELECT 
    p.project_id,
    p.title,
    p.category,
    COUNT(c.commit_id) as commit_count,
    MAX(c.commit_date) as last_update
  FROM projects p
  LEFT JOIN commits c ON p.project_id = c.project_id
  GROUP BY p.project_id, p.title, p.category
),
TechStack AS (
  SELECT 
    pt.project_id,
    STRING_AGG(t.tech_name, ', ') as technologies
  FROM project_technologies pt
  JOIN technologies t ON pt.tech_id = t.tech_id
  GROUP BY pt.project_id
)
SELECT 
  ps.*,
  ts.technologies,
  u.username as owner
FROM ProjectStats ps
JOIN TechStack ts ON ps.project_id = ts.project_id
JOIN users u ON ps.owner_id = u.user_id
WHERE ps.category = 'web'
  AND ps.commit_count > 10
ORDER BY ps.last_update DESC;`;

  const backendCode = `
// Node.js Backend
const express = require('express');
const router = express.Router();

router.get('/api/projects', async (req, res) => {
  try {
    const projects = await Project.find()
      .sort({ createdAt: -1 })
      .limit(10);
      
    res.json(projects);
  } catch (error) {
    res.status(500).json({
      message: 'Error fetching projects'
    });
  }
});

// Authentication Middleware
const auth = async (req, res, next) => {
  try {
    const token = req.header('Authorization');
    if (!token) throw new Error();
    
    const decoded = jwt.verify(token, process.env.JWT_SECRET);
    const user = await User.findOne({ _id: decoded.id });
    
    if (!user) throw new Error();
    
    req.user = user;
    next();
  } catch (error) {
    res.status(401).send({ error: 'Please authenticate.' });
  }
};`;

  useEffect(() => {
    if (frontendTypedRef.current) {
      frontendTypedRef.current.destroy();
    }
    if (sqlTypedRef.current) {
      sqlTypedRef.current.destroy();
    }
    if (backendTypedRef.current) {
      backendTypedRef.current.destroy();
    }

    frontendTypedRef.current = new Typed(frontendCodeRef.current, {
      strings: [frontendCode],
      typeSpeed: 30,
      backSpeed: 30,
      fadeOut: true,
      loop: true,
      showCursor: true,
      smartBackspace: false
    });

    sqlTypedRef.current = new Typed(sqlCodeRef.current, {
      strings: [sqlCode],
      typeSpeed: 30,
      backSpeed: 30,
      fadeOut: true,
      loop: true,
      showCursor: true,
      smartBackspace: false
    });

    backendTypedRef.current = new Typed(backendCodeRef.current, {
      strings: [backendCode],
      typeSpeed: 30,
      backSpeed: 30,
      fadeOut: true,
      loop: true,
      showCursor: true,
      smartBackspace: false
    });

    return () => {
      if (frontendTypedRef.current) {
        frontendTypedRef.current.destroy();
      }
      if (sqlTypedRef.current) {
        sqlTypedRef.current.destroy();
      }
      if (backendTypedRef.current) {
        backendTypedRef.current.destroy();
      }
    };
  }, []);

  return (
    <div className="code-background">
      <div className="code-left">
        <pre>
          <code ref={frontendCodeRef}></code>
        </pre>
      </div>
      <div className="code-middle">
        <pre>
          <code ref={sqlCodeRef}></code>
        </pre>
      </div>
      <div className="code-right">
        <pre>
          <code ref={backendCodeRef}></code>
        </pre>
      </div>
    </div>
  );
};

export default CodeBackground;
